import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
	catchError, concatMap, debounceTime, exhaustMap, filter, retryWhen, switchMap, takeUntil, withLatestFrom
} from 'rxjs/operators';
import { EMPTY, from, of } from 'rxjs';
import * as ProviderActions from './provider.actions';
import { ProviderFacade } from './provider.facade';

import { ignoreResultOnActions, isNullOrEmpty, isNullOrUndefined, ResourceUtils } from '@ap-angular/shared/utils';
import {
	AbsentHourDTO, AbsentHoursService, AttachmentsResponseDTO, CalendarSettingsDTO, ExpertiseDTO,
	ExtraInfoDTO, KeyValueDTO,
	KeyValuePairService, KeyValueTypeEnumDTO, MedicalServiceDTO, MedicalServiceService, MessageTemplateDTO, MessageTemplateService,
	PeriodicalDeletionService, PersonDTO, PersonListInfoDTO, PersonService,
	PostalCodeCityDTO,
	RegisterProfileConfirmResponseDTO,
	RegistrationRequestDTO, ResourceDTO, ResourceGroupDTO, ResourceHoursRequestDTO, ResourceHoursResponseDTO, ResourceOverviewService,
	ResourceService, SettingsDTO, SettingsService, TimeSlotsDTO, TreatmentAssociationDTO, TreatmentAssociationService, TreatmentCategoryDTO, TreatmentCategoryService, TreatmentDTO, TreatmentsService,
	WorkScheduleDTO,
	WorkScheduleService
} from '@auto-generated/ap-platform/care-providers';
import {
  AppointmentDTO,
  AppointmentHeatmapDTO,
  BusinessHoursRequestDTO,
  CalendarEventDTO,
  ClassicAppointmentService,
  ImageDTO,
  MessageDTO,
  MessageService,
  OnlineAppointmentService,
  PageableAppointmentInfosResponseDTO,
  PageableMessagesRequestDTO,
  PageableResponseDTO,
  PageableWaitingListsResponseDTO,
  PublicAppointmentDTO,
  ReportsAdminInstitutionCountsDTO,
  ReportsGroupedDataDTO,
  ReportsGroupingKeyEnumDTO,
  ReportsRequestDTO,
  ReportsService,
  ReportsTotalCountsDTO,
  ReportsUserAppointmentsDTO,
  ReportsUserAppointmentsFilterDTO,
  ReportsUserAppointmentsRequestDTO,
  ResourceGdprDTO,
  WaitingListBookingStatusResponseDTO,
  WaitingListDTO,
  WaitingListService
} from '@auto-generated/ap-platform/online-appointments';
import {
	ContentManagementSystemService, PageableUsersResponseDTO, PasswordResetResponseDTO, UserDTO, UserService
} from '@auto-generated/ap-platform/users';
import { InternalUtils } from '../internal.utils';
import {
	GetPatientsResponseDTO, PageablePatientsResponseDTO, PatientDTO, PatientGdprInfoDTO, PatientService
} from '@auto-generated/ap-platform/patients';

@Injectable()
export class ProviderEffects {

	autocompletePostalCodeCity$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.AutocompletePostalCodeCity),
		switchMap(({ payload }) => {
			return this.keyValuePairServiceApi.searchPlzOrt(payload).pipe(
				switchMap((result: Array<PostalCodeCityDTO>) =>
					of(ProviderActions.AutocompletePostalCodeCityComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.AutocompletePostalCodeCityFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadKeySalutation$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProviderKeySalutation),
		exhaustMap(() => {
			return this.keyValuePairServiceApi.getKeysOfType(KeyValueTypeEnumDTO.SALUTATION).pipe(
				switchMap((result: Array<KeyValueDTO>) =>
					of(ProviderActions.LoadProviderKeySalutationComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderKeySalutationFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadKeyTitle$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProviderKeyTitle),
		exhaustMap(() => {
			return this.keyValuePairServiceApi.getKeysOfType(KeyValueTypeEnumDTO.TITLE).pipe(
				switchMap((result: Array<KeyValueDTO>) =>
					of(ProviderActions.LoadProviderKeyTitleComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderKeyTitleFailed({ error })
					])
				)
			);
		})
	),
		{ useEffectsErrorHandler: false }
	);

	loadKeyAcademicalDegree$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProviderKeyAcademicalDegree),
		exhaustMap(() => {
			return this.keyValuePairServiceApi.getKeysOfType(KeyValueTypeEnumDTO.ACADEMICALDEGREE).pipe(
				switchMap((result: Array<KeyValueDTO>) =>
					of(ProviderActions.LoadProviderKeyAcademicalDegreeComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderKeyAcademicalDegreeFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProvider),
		exhaustMap(({ payload }) => {
			return this.personServiceApi.readPerson(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PersonDTO) =>
					of(ProviderActions.LoadProviderComplete({ data: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadProviderList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProviderList, ProviderActions.ClearReinitializedProvider),
		switchMap(({ payload }) => {
			return this.personServiceApi.readPersonsByUsername(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<PersonListInfoDTO>) =>
					of(ProviderActions.LoadProviderListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderListFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateProvider),
		switchMap(({ payload }) => {
			return this.personServiceApi.createPerson(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PersonDTO) =>
					of(ProviderActions.CreateProviderComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateProviderFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	confirmRegistrationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ConfirmRegistrationRequest),
		switchMap(({ payload: { registerProfileConfirm, token } }) => {
			return this.personServiceApi.confirmRegistrationRequest(token, registerProfileConfirm).pipe(
				concatMap((response: RegisterProfileConfirmResponseDTO) =>
					of(ProviderActions.ConfirmRegistrationRequestComplete({ payload: { registerProfile: registerProfileConfirm, redirectPartner: response.redirectPartner } }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ConfirmRegistrationRequestFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveProviderInformation),
		switchMap(({ payload: { id, body } }) => {
			return this.personServiceApi.patchPerson(id, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PersonDTO) =>
					of(ProviderActions.SaveProviderInformationComplete({ data: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.SaveProviderInformationFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveProviderImage$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveProviderImage),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			const original$ = this.personServiceApi.uploadProfileImage(provider.id, action.payload['original'], true);
			const thumbnail$ = this.personServiceApi.uploadProfileImage(provider.id, action.payload['thumbnail'], false);
			return original$.pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				concatMap(() => thumbnail$),
				switchMap((thumbnail) =>
					of(ProviderActions.SaveProviderImageComplete({ payload: thumbnail }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.SaveProviderImageFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteProviderImage$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteProviderImage),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.deleteProfileImage(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(result =>
					of(ProviderActions.DeleteProviderImageComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteProviderImageFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveProviderSchedule$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveProviderSchedule),
		switchMap(({ payload: { id, body } }) => {
			return this.personServiceApi.updateOpenHours(id, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.SaveProviderScheduleComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.SaveProviderScheduleFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Services ========================================================== */

	loadProviderService$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadProviderServices),
		switchMap(({ payload }) => {
			return this.medicalServicesService.getMedicalServices(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<MedicalServiceDTO>) =>
					of(ProviderActions.LoadProviderServicesComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadProviderServicesFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveProviderService$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveProviderService),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.medicalServicesService.createMedicalService(provider.id, action.payload).pipe(
				debounceTime(2000),
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: MedicalServiceDTO) =>
					of(ProviderActions.SaveProviderServiceComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({error}),
						ProviderActions.SaveProviderServiceFailed({error: error})
					])
				)
			);
		})),
		{useEffectsErrorHandler: false}
	);

	deleteProviderService$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteProviderService),
		switchMap((action: any) => {
			return this.medicalServicesService.deleteMedicalService(action.payload).pipe(
				debounceTime(2000),
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() =>
					of(ProviderActions.DeleteProviderServiceComplete())
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteProviderServiceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Expertise ========================================================== */

	loadExpertise$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadExpertise),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.getExpertise(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ExpertiseDTO) =>
					of(ProviderActions.LoadExpertiseComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadExpertiseFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveExpertise$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveExpertise),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.updateExpertise(provider.id, action.payload).pipe(
				debounceTime(2000),
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ExpertiseDTO) =>
					from([
						ProviderActions.LoadExpertiseComplete({ payload: result }),
						ProviderActions.SaveExpertiseComplete({ payload: result })
					])
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.SaveExpertiseFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Exrta Info ========================================================== */

	loadExtraInfo$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadExtraInfo),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.getExtraInfo(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ExtraInfoDTO) =>
					of(ProviderActions.LoadExtraInfoComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadExtraInfoFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveExtraInfo$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveExtraInfo),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.updateExtraInfo(provider.id, action.payload).pipe(
				debounceTime(2000),
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ExtraInfoDTO) =>
					from([
						ProviderActions.LoadExtraInfoComplete({ payload: result }),
						ProviderActions.SaveExtraInfoComplete({ payload: result })
					])
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.SaveExtraInfoFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Messages ========================================================== */

	countUnreadMessage$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CountUnreadMessage),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.messageService.getUnreadMessagesNumber(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((number: number) =>
					of(ProviderActions.CountUnreadMessageComplete({ payload: number }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CountUnreadMessageFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadMessageList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadMessageList),
		switchMap(({ payload }) => {
			return this.messageService.getMessagesPaginated(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PageableResponseDTO) =>
					of(ProviderActions.LoadMessageListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadMessageListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadLastMessage$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadLastMessage),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			const messagePageableRequest: PageableMessagesRequestDTO = {
				page: action.payload.pageIndex,
				size: 1,
				personId: provider.id
			};
			return this.messageService.getMessagesPaginated(messagePageableRequest).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PageableResponseDTO) =>
					of(ProviderActions.LoadLastMessageComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadLastMessageFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadMessage$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadMessage),
		switchMap((action) => {
			return this.messageService.getMessageById(action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((message: MessageDTO) =>
					of(ProviderActions.LoadMessageComplete({ payload: message }))
				),
				retryWhen(() => EMPTY),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadMessageFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteProvider),
		switchMap(({ payload }) => {
			return this.personServiceApi.deletePerson(payload.providerId, payload.email, payload.download, payload.xSmsSimulate).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.DeleteProviderComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteProviderFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	archiveProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ArchiveProvider),
		switchMap(({ payload }) => {
			return this.personServiceApi.archivePerson(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.ArchiveProviderComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ArchiveProviderFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	reactivateProvider$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ReactivateProvider),
		switchMap(({ payload }) => {
			return this.personServiceApi.reactivatePerson(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.ReactivateProviderComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ReactivateProviderFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	copyProviderData$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CopyProviderData),
		exhaustMap(({ payload }) => {
			return this.personServiceApi.copyPersonData(payload.idFrom, payload.idTo, payload.data).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.CopyProviderDataComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CopyProviderDataFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Appointment ========================================================== */
	//region Appointment

	loadResourceHourList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.loadResourceHourList),
		withLatestFrom(
			this.providerFacade.data$,
			this.providerFacade.calendarResources$
		),
		filter(([action, provider, resources]: [any, PersonDTO, Array<ResourceDTO>]) => !isNullOrUndefined(provider && (resources || action.payload.resourceId))),
		// TODO: Blocking sometimes?!
		//distinctUntilChanged((prev, next) => {
		//	return JSON.stringify(prev[0].payload) === JSON.stringify(next[0].payload) &&
		//		   JSON.stringify(prev[1].id) === JSON.stringify(next[1].id) &&
		//		   JSON.stringify(prev[2]) === JSON.stringify(next[2]);
		//}),
		switchMap(([action, provider, resources]: [any, PersonDTO, Array<ResourceDTO>]) => {
			const payload: ResourceHoursRequestDTO = {
				careProviderId: provider.id,
				resourceIds: !isNullOrUndefined(resources) && isNullOrUndefined(action.payload.resourceId) ?
					resources.map(resource => resource.id) : [action.payload.resourceId],
				interval: action.payload.interval,
				startDate: this.datePipe.transform(action.payload.date, 'yyyy-MM-dd'),
				standardSchedulePeriod: action.payload.bookingHours,
				onlineSchedulePeriod: action.payload.onlineBookingHours,
				internalSchedulePeriod: action.payload.internalBookingHours,
				absentHours: action.payload.absentHours
			};
			return this.resourceOverviewService.resourceOverview(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<ResourceHoursResponseDTO>) =>
					of(ProviderActions.loadResourceHourListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.loadResourceHourListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadAppointmentList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadAppointmentList),
		withLatestFrom(
			this.providerFacade.data$,
			this.providerFacade.calendarResources$
		),
		// TODO: Reload when add appointment with same data. Quick fix disabled below
		//distinctUntilChanged((prev, next) => {
		//	return JSON.stringify(prev[0].payload) === JSON.stringify(next[0].payload) &&
		//		   JSON.stringify(prev[1].id) === JSON.stringify(next[1].id) &&
		//		   JSON.stringify(prev[2]) === JSON.stringify(next[2]);
		//}),
		filter(([action, provider, resources]:
			[any, PersonDTO, Array<ResourceDTO>]) => !isNullOrUndefined(provider && resources) && resources.length > 0),
		switchMap(([action, provider, resources]: [any, PersonDTO, Array<ResourceDTO>]) => {
			const resourcesArray = resources.map(resource => resource.id);
			const payload: BusinessHoursRequestDTO = {
				careProviderId: provider.id,
				resourceIds: resourcesArray,
				interval: action.payload.interval,
				startDate: action.payload.date.toISOString()
			};
			return this.onlineAppointmentApi.readAppointments(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<CalendarEventDTO>) =>
					of(ProviderActions.LoadAppointmentListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadAppointmentListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadAppointmentHeatmap$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadAppointmentHeatmap),
		filter((action) => !!action.payload && !isNullOrEmpty(action.payload.resourceIds)),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			const payload: BusinessHoursRequestDTO = {
				careProviderId: provider.id,
				resourceIds: action.payload.resourceIds,
				interval: action.payload.interval,
				startDate: action.payload.date.toISOString()
			};
			return this.onlineAppointmentApi.readAppointmentHeatmap(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: Array<AppointmentHeatmapDTO>) =>
					of(ProviderActions.LoadAppointmentHeatmapComplete({ payload: next }))),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadAppointmentHeatmapFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadAppointment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadAppointment),
		switchMap((action) => {
			return this.classicAppointmentApi.getAppointment(action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: AppointmentDTO) =>
					of(ProviderActions.LoadAppointmentComplete({ payload: next }))),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadAppointmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	checkAppointmentInterval$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CheckAppointmentInterval),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.classicAppointmentApi.checkSlot(action.overlap, { ...action.payload, careProviderId: provider.id }).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.CheckAppointmentIntervalComplete({ payload: next }))),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CheckAppointmentIntervalFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadPublicAppointmentList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadPublicAppointmentList),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.classicAppointmentApi.getPublicAppointmentsByIds(action.payload, provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PublicAppointmentDTO[]) =>
					of(ProviderActions.LoadPublicAppointmentListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadPublicAppointmentListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createMultipleAppointments$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateMultipleAppointments),
		exhaustMap(({ payload: { appointments, bookingLink, allowAppointmentOverlap } }) => {
			return this.classicAppointmentApi.createMultipleAppointments(bookingLink, allowAppointmentOverlap, appointments).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				exhaustMap((next: Array<AppointmentDTO>) =>
					of(ProviderActions.CreateMultipleAppointmentsComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateMultipleAppointmentsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createAppointment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateAppointment),
		exhaustMap(({ payload: { appointment, bookingLink, waitingListId, allowAppointmentOverlap } }) => {
			return this.classicAppointmentApi.createAppointment(bookingLink, allowAppointmentOverlap, appointment, waitingListId).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				exhaustMap((next: AppointmentDTO) =>
					of(ProviderActions.CreateAppointmentComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateAppointmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateAppointment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateAppointment),
		exhaustMap(({ payload: { id, sendSms, body } }) => {
			// TODO - allowAppointmentsOverlap is currently true by default also on the backend side.
			return this.classicAppointmentApi.patchAppointment(id, true, body, sendSms).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: AppointmentDTO) =>
					of(ProviderActions.UpdateAppointmentComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateAppointmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteAppointment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteAppointment),
		exhaustMap((action) => {
			return this.onlineAppointmentApi.cancelAppointment(action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() =>
					of(ProviderActions.DeleteAppointmentComplete())
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteAppointmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);
	// endregion

	loadSystemTemplates$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadSystemTemplates),
		exhaustMap(() => {
			return this.messageTemplateApi.getMessageTemplates(true).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<MessageTemplateDTO>) =>
					of(ProviderActions.LoadSystemTemplatesComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadSystemTemplatesFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadCustomTemplates$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadCustomTemplates),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.messageTemplateApi.getMessageTemplates(false, provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<MessageTemplateDTO>) =>
					of(ProviderActions.LoadCustomTemplatesComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadCustomTemplatesFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	saveCustomTemplate$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.SaveCustomTemplate),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			const messageTemplate: MessageTemplateDTO = Object.assign({}, action.payload, { person: provider.id });
			if (isNullOrUndefined(messageTemplate.id)) {
				return this.messageTemplateApi.createMessageTemplate(messageTemplate).pipe(
					takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
					switchMap((result: MessageTemplateDTO) =>
						of(ProviderActions.SaveCustomTemplateComplete({ payload: result }))
					),
					catchError((error) =>
						from([
							ProviderActions.Error({ error }),
							ProviderActions.SaveCustomTemplateFailed({ error: error })
						])
					)
				);
			} else {
				return this.messageTemplateApi.updateMessageTemplate(messageTemplate).pipe(
					takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
					switchMap((result: MessageTemplateDTO) =>
						of(ProviderActions.SaveCustomTemplateComplete({ payload: result }))
					),
					catchError((error) =>
						from([
							ProviderActions.Error({ error }),
							ProviderActions.SaveCustomTemplateFailed({ error: error })
						])
					)
				);
			}
		})),
		{ useEffectsErrorHandler: false }
	);

	uploadMissingHours$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UploadAbsentHours),
		exhaustMap(({ payload, filename }) => {
			return this.absenceHourService.uploadAbsentHours(payload, filename).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: AbsentHourDTO[]) =>
					of(ProviderActions.UploadAbsentHoursComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UploadAbsentHoursFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createAbsentHours$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateAbsentHours),
		exhaustMap(({ payload }) => {
			return this.absenceHourService.createAbsentHours(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: AbsentHourDTO[]) =>
					of(ProviderActions.CreateAbsentHoursComplete())
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateAbsentHoursFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	uploadAttachment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UploadAttachment),
		exhaustMap(({ payload }) => {
			return this.messageTemplateApi.uploadAttachment(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: AttachmentsResponseDTO) =>
					of(ProviderActions.UploadAttachmentComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UploadAttachmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getAttachment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetAttachment),
		exhaustMap(({ payload: { id, uuid } }) => {
			return this.messageTemplateApi.getAttachment(id, uuid).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Blob) =>
					of(ProviderActions.GetAttachmentComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GetAttachmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Resources ========================================================== */

	createResource$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateResource),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.resourceList$),
		switchMap(([action, provider, resources]) => {
			const resource: ResourceDTO = Object.assign({}, action.payload, { personId: provider.id });
			return this.resourceService.createResource(resource).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceDTO) =>
					of(ProviderActions.CreateResourceComplete({
						resources: InternalUtils.getModifiedResourceState(result, resources),
						result: result
					}))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateResourceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteResource$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteResource),
		withLatestFrom(this.providerFacade.resourceList$, this.providerFacade.calendarResources$),
		switchMap(([action, resources, calendarResources]) => {
			return this.resourceService.deleteResource(action.payload.resource.id, action.payload.email, action.payload.download).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(blob =>
					of(ProviderActions.DeleteResourceComplete({
						payload: {
							resources: InternalUtils.getModifiedResourceState(action.payload.resource, resources, true),
							calendarResources: !!calendarResources ?
								[...calendarResources.filter(resource => resource.id !== action.payload.resource.id)] : [],
							pdf: blob
						}
					})
					)
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteResourceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateResource$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateResource),
		withLatestFrom(this.providerFacade.resourceList$),
		switchMap(([action, resourceList]) => {
			return this.resourceService.patchResource(action.payload.id, action.payload.body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceDTO) =>
					of(ProviderActions.UpdateResourceComplete({ resources: InternalUtils.getModifiedResourceState(result, resourceList) }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateResourceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	reorderResource$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ReorderResources),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.resourceList$, this.providerFacade.calendarResources$),
		switchMap(([action, provider, resources, calendarResources]) => {
			return this.resourceService.reorderResources(provider.id, action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() => {
					const RESOURCES = InternalUtils.getReorderedResourceState(action.payload, resources);
					return of(ProviderActions.ReorderResourcesComplete({
						payload: {
							calendarResources: ResourceUtils.calendarResourcesIndexUpdated(calendarResources, RESOURCES),
							resources: RESOURCES
						}
					}))
				}
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ReorderResourcesFailed({ error: error })
					])
				)
			);
		}))
	);

	loadResource$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadResource),
		switchMap((action: any) => {
			return this.resourceService.readResource(action.payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceDTO) =>
					of(ProviderActions.LoadResourceComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadResourceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadResourceGroup$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadResourceGroup),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.resourceService.readAllResources(provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceGroupDTO) =>
					of(ProviderActions.LoadResourceGroupComplete({ resources: [...result.resources] }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadResourceGroupFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadAllResourceListByType$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadAllResourceListByType),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.resourceService.readAllResources(provider.id, action.payload.type).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceGroupDTO) => {
					const resources = result.resources.filter((group) => group.type === action.payload.type)[0];
					const resourceList = resources && resources['items'] && resources['items'].length > 0 ? resources['items'] : [];

					return of(ProviderActions.LoadAllResourceListByTypeComplete({ resources: resourceList }))
				}),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadResourceGroupFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);


	getResourceWorkSchedules$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetResourceWorkSchedules),
		switchMap(({ payload: { resourceId } }) => {
			return this.workScheduleService.getResourceWorkSchedules(resourceId).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<WorkScheduleDTO>) =>
					of(ProviderActions.GetResourceWorkSchedulesComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GetResourceWorkSchedulesFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createWorkSchedule$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateWorkSchedule),
		switchMap(({ payload: { resourceId, workSchedule } }) => {
			return this.workScheduleService.createWorkSchedule(resourceId, workSchedule).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: WorkScheduleDTO) =>
					of(ProviderActions.CreateWorkScheduleComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateWorkScheduleFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateWorkSchedule$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateWorkSchedule),
		switchMap(({ payload: { id, workSchedule } }) => {
			return this.workScheduleService.updateWorkSchedule(id, workSchedule).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: WorkScheduleDTO) =>
					of(ProviderActions.UpdateWorkScheduleComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateWorkScheduleFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteWorkScheduleHours$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteWorkScheduleHours),
		switchMap(({ payload }) => {
			return this.workScheduleService.deleteWorkSchedule(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.DeleteWorkScheduleHoursComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteWorkScheduleHoursFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadResourceAbsences$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadResourceAbsences),
		switchMap(({ payload: { resourceId } }) => {
			return this.absenceHourService.getAbsentHours(resourceId).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<AbsentHourDTO>) =>
					of(ProviderActions.LoadResourceAbsencesComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadResourceAbsencesFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createResourceAbsence$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateResourceAbsence),
		switchMap(({ payload: { resourceId, absenceHours, allowOverlapping } }) => {
			return this.absenceHourService.createAbsentHour(resourceId, absenceHours, allowOverlapping).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: AbsentHourDTO) =>
					of(ProviderActions.CreateResourceAbsenceComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateResourceAbsenceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);


	patchResourceAbsence$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.PatchResourceAbsence),
		switchMap(({ payload: { id, absenceHours, allowOverlapping } }) => {
			return this.absenceHourService.patchAbsentHour(id, absenceHours, allowOverlapping).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: AbsentHourDTO) =>
					of(ProviderActions.PatchResourceAbsenceComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.PatchResourceAbsenceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteResourceAbsence$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteResourceAbsence),
		switchMap(({ payload }) => {
			return this.absenceHourService.deleteAbsentHour(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: any) =>
					of(ProviderActions.DeleteResourceAbsenceComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteResourceAbsenceFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadContentCMS = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadContent),
		switchMap(({ payload: { type, pageType } }) => {
			return this.cmsServiceApi.getPageContent(type, pageType).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((response: any) =>
					of(ProviderActions.LoadContentComplete({ payload: response }))
				),
				catchError((error) => {
					return from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadContentFailed({ error: error })
					]);
				})
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getResourceGDPR$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadResourceGdpr),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.classicAppointmentApi.getResourceGdprInfo(provider.id, action.payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceGdprDTO) =>
					of(ProviderActions.LoadResourceGdprComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadResourceGdprFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Calendar Settings ========================================================== */

	loadSettings$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadSettings),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.settingsCalendar$),
		switchMap(([action, provider, settingsCalendar]: [any, PersonDTO, CalendarSettingsDTO]) => {
			return this.settingsService.getPersonSettings(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settingsAppointment: SettingsDTO) => {
					delete settingsAppointment.calendarSettings;
					return of(ProviderActions.LoadSettingsComplete({
						settings: {
							appointment: settingsAppointment,
							calendar: settingsCalendar
						}
					}));
				}),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadSettingsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateSettings$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateSettings),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.settingsCalendar$),
		switchMap(([{ payload: { body } }, provider, settingsCalendar]: [any, PersonDTO, CalendarSettingsDTO]) => {
			return this.settingsService.patchPersonSettings(provider.id, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settingsAppointment: SettingsDTO) => {
					delete settingsAppointment.calendarSettings;
					return of(ProviderActions.UpdateSettingsComplete({
						settings: {
							appointment: settingsAppointment,
							calendar: settingsCalendar
						}
					}));
				}),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateSettingsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadCalendarSettings$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadCalendarSettings),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.settings$),
		switchMap(([{ payload }, provider, settingsAppointment]: [any, PersonDTO, SettingsDTO]) => {
			return this.settingsService.getCalendarSettings(provider.id, payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settingsCalendar: CalendarSettingsDTO) =>
					of(ProviderActions.LoadCalendarSettingsComplete({
						settings: {
							appointment: settingsAppointment,
							calendar: settingsCalendar
						}
					}))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadCalendarSettingsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateCalendarSettings$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateCalendarSettings),
		withLatestFrom(this.providerFacade.data$, this.providerFacade.settings$),
		switchMap(([{ payload: { body, username } }, provider, settingsAppointment]: [any, PersonDTO, SettingsDTO]) => {
			return this.settingsService.patchCalendarSettings(provider.id, body, username).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settingsCalendar: CalendarSettingsDTO) =>
					of(ProviderActions.UpdateCalendarSettingsComplete({
						settings: {
							appointment: settingsAppointment,
							calendar: settingsCalendar
						}
					}))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateCalendarSettingsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadCalendarDefaultSettings$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadCalendarDefaultSettings),
		switchMap(() => {
			return this.settingsService.getDefaultSettings().pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settings: SettingsDTO) =>
					of(ProviderActions.LoadCalendarDefaultSettingsComplete({ payload: settings }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadCalendarDefaultSettingsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	downloadPrintableCalendar$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DownloadPrintableCalendar),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			return this.onlineAppointmentApi.calendarPrint({ ...action.payload, careProviderId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Blob) =>
					of(ProviderActions.DownloadPrintableCalendarComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DownloadPrintableCalendarFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadDefaultTimeSlots$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadDefaultTimeSlots),
		switchMap(() => {
			return this.settingsService.getDefaultTimeSlots().pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((settings: TimeSlotsDTO) =>
					of(ProviderActions.LoadDefaultTimeSlotsComplete({ payload: settings }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadDefaultTimeSlotsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	downloadPeriodicalDeletionReport$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DownloadPeriodicalDeletionReport),
		withLatestFrom(this.providerFacade.data$),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			return this.periodicalDeletionService.getPeriodicalDeletionReport(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((blob) =>
					of(ProviderActions.DownloadPeriodicalDeletionReportComplete({ payload: blob }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DownloadPeriodicalDeletionReportFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Treatment ========================================================== */
	//region Treatment

	loadTreatmentList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadTreatmentList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.treatmentService.getPersonTreatments(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<TreatmentDTO>) =>
					of(ProviderActions.LoadTreatmentListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadTreatmentListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadTreatmentCategoryList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadTreatmentCategoryList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.treatmentCategoryService.getTreatmentCategories(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: Array<TreatmentCategoryDTO>) =>
					of(ProviderActions.LoadTreatmentCategoryListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadTreatmentCategoryListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createTreatment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateTreatment),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.treatmentService.createTreatment(provider.id, action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentDTO) =>
					of(ProviderActions.CreateTreatmentComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateTreatmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createTreatmentAssociations$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateTreatmentAssociations),
		switchMap(({ payload: { treatmentId, body } }) => {
			return this.treatmentAssociationService.createAssociationsForTreatment(treatmentId, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentAssociationDTO[]) =>
					of(ProviderActions.CreateTreatmentAssociationsComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateTreatmentAssociationsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateTreatmentAssociations$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateTreatmentAssociations),
		switchMap(({ payload: { treatmentId, body } }) => {
			return this.treatmentAssociationService.updateTreatmentAssociations(treatmentId, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentAssociationDTO[]) =>
					of(ProviderActions.UpdateTreatmentAssociationsComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateTreatmentAssociationsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteTreatmentAssociations$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteTreatmentAssociations),
		switchMap(({ payload }) => {
			return this.treatmentAssociationService.deleteTreatmentAssociations(payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() =>
					of(ProviderActions.DeleteTreatmentAssociationsComplete({ payload }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteTreatmentAssociationsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);


	createCategory$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateCategory),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.treatmentCategoryService.createTreatmentCategory(provider.id, action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentCategoryDTO) =>
					of(ProviderActions.CreateCategoryComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateCategoryFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateTreatment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateTreatment),
		switchMap(({ payload: { id, body } }) => {
			return this.treatmentService.patchTreatment(id, body).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentDTO) =>
					of(ProviderActions.UpdateTreatmentComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateTreatmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateCategory$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateCategory),
		switchMap(({ payload: { category } }) => {
			return this.treatmentCategoryService.updateTreatmentCategory(category.id, category).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: TreatmentCategoryDTO) =>
					of(ProviderActions.UpdateCategoryComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateCategoryFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteTreatment$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteTreatment),
		switchMap((action) => {
			return this.treatmentService.deleteTreatment(action.payload.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() =>
					of(ProviderActions.DeleteTreatmentComplete({ payload: action.payload }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteTreatmentFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteCategory$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteCategory),
		switchMap((action) => {
			return this.treatmentCategoryService.deleteTreatmentCategory(action.payload.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(() =>
					of(ProviderActions.DeleteCategoryComplete({ payload: action.payload }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteCategoryFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getPatientList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetPatientList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.searchPatients({ ...action.payload, careProviderId: provider.id }).pipe(
				switchMap((result: PageablePatientsResponseDTO) =>
					of(ProviderActions.GetPatientListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GetPatientListFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getPatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadPatientDetails),
		switchMap((action) => {
			return this.patientService.getPatientById(action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PatientDTO) =>
					of(ProviderActions.LoadPatientDetailsComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadPatientDetailsFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getPatientAppointmentList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadPatientAppointmentList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.classicAppointmentApi.getAppointments({ ...action.payload, careProviderId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PageableAppointmentInfosResponseDTO) =>
					of(ProviderActions.LoadPatientAppointmentListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadPatientAppointmentListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createPatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreatePatient),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.createPatient({ ...action.payload, careProviderId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PatientDTO) =>
					of(ProviderActions.CreatePatientComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreatePatientFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updatePatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdatePatient),
		switchMap((action) => {
			return this.patientService.updatePatient(action.payload.id, action.payload.data).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: PatientDTO) =>
					from([
						ProviderActions.UpdatePatientComplete({ payload: result }),
						ProviderActions.LoadPatientDetailsComplete({ payload: result })
					])
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdatePatientFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getPatientGDPR$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetPatientGdpr),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.getPatientGdprInfo(provider.id, action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PatientGdprInfoDTO) =>
					of(ProviderActions.GetPatientGdprComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GetPatientGdprFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deletePatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeletePatient),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			const payload = action.payload;
			return this.patientService.deletePatients(provider.id, payload.patientList, payload.patientName, payload.email, payload.download).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap(blob =>
					of(ProviderActions.DeletePatientComplete({ payload: blob }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeletePatientFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	blockPatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.BlockPatient),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.blockPatient(action.payload.id, { careProviderId: provider.id, block: action.payload.block }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.BlockPatientComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.BlockPatientFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	getMergePatientList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetMergePatientList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.getMergingPatients(action.payload, provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: GetPatientsResponseDTO) =>
					of(ProviderActions.GetMergePatientListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						//ErrorActions.HttpAction({errorFrom: ProviderActions.GetMergePatientListFailed, error: error}),
						ProviderActions.GetMergePatientListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	mergePatient$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.MergePatient),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !isNullOrUndefined(provider)),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			return this.patientService.mergePatients({ ...action.payload, careProviderId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PatientDTO) =>
					of(ProviderActions.MergePatientComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						//ErrorActions.HttpAction({errorFrom: ProviderActions.MergePatientFailed, error: error}),
						ProviderActions.MergePatientFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== User Management ========================================================== */

	userRequestList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadUserRequestList),
		debounceTime(200),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.findAllRequestsForCareProvider(provider.id).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: RegistrationRequestDTO[]) =>
					of(ProviderActions.LoadUserRequestListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadUserRequestListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	userList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadUserList),
		debounceTime(200),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider && !!action.payload),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.userServiceApi.searchUsersPaginated({ ...action.payload, personId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PageableUsersResponseDTO) =>
					of(ProviderActions.LoadUserListComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadUserListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createRegistrationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateRegistrationRequest),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider && !!action.payload),
		exhaustMap(([action, provider]: [any, PersonDTO]) => {
			return this.personServiceApi.createRegistrationRequest(provider.id, action.payload).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.CreateRegistrationRequestComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateRegistrationRequestFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	resendVerificationMail$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ResendVerificationMail),
		exhaustMap(({ payload }) => {
			return this.personServiceApi.updateRegistrationStatus(payload.id, payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.ResendVerificationMailComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ResendVerificationMailFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteRegistrationRequest$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteRegistrationRequest),
		exhaustMap(({ payload }) => {
			return this.personServiceApi.deleteRegistrationRequest(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.DeleteRegistrationRequestComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteRegistrationRequestFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	deleteUser$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteUser),
		switchMap(({ payload: { username, personId } }) => {
			return this.userServiceApi.deleteUser(username, personId).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next) =>
					of(ProviderActions.DeleteUserComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteUserFailed({ error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadUser$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadUser),
		withLatestFrom(this.providerFacade.data$),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.userServiceApi.readUser(action.payload, provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: UserDTO) =>
					of(ProviderActions.LoadUserComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadUserFailed({ error: error })
					])
				)
			);
		})
	),
		{ useEffectsErrorHandler: false }
	);

	loadNotAssignedResourceList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadNotAssignedResourceList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.resourceService.getNotAssignedResources(provider.id, action.payload.userId).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((result: ResourceDTO[]) =>
					of(ProviderActions.LoadNotAssignedResourceListComplete({ payload: result }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadNotAssignedResourceListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateUser$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateUser),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.userServiceApi.patchUser(action.payload.username, action.payload.data, provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: UserDTO) =>
					of(ProviderActions.UpdateUserComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateUserFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	resetPassword$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ResetPassword),
		exhaustMap(({ payload }) => {
			return this.userServiceApi.requestPasswordReset(payload).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PasswordResetResponseDTO) =>
					of(ProviderActions.ResetPasswordComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ResetPasswordFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	pageableWaitingList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadPageableWaitingList),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider && !!action.payload),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.waitingListService.getWaitingListsPaginated({ ...action.payload, personId: provider.id }).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PageableWaitingListsResponseDTO) =>
					of(ProviderActions.LoadPageableWaitingListComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadPageableWaitingListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	createWaitingList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.CreateWaitingList),
		exhaustMap(({ payload: { waitingList, bookingLink } }) => {
			return this.waitingListService.createWaitingList(bookingLink, waitingList).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				exhaustMap((next: WaitingListDTO) =>
					of(ProviderActions.CreateWaitingListComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.CreateWaitingListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	updateWaitingList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UpdateWaitingList),
		exhaustMap(({ payload: { id, waitingList } }) => {
			return this.waitingListService.patchWaitingList(id, waitingList).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				exhaustMap((next: WaitingListDTO) =>
					of(ProviderActions.UpdateWaitingListComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UpdateWaitingListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);


	deleteWaitingList$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.DeleteWaitingList),
		switchMap(({ payload }) => {
			return this.waitingListService.deleteWaitingList(payload.waitingListId).pipe(
				ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: PageableWaitingListsResponseDTO) =>
					of(ProviderActions.DeleteWaitingListComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.DeleteWaitingListFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	loadWaitingListAttachments$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.LoadWaitingListAttachments),
		switchMap(({ payload: { waitingListId, attachments } }) => {
			return this.waitingListService.getWaitingListAttachmentByUuid(waitingListId, attachments).pipe(
				switchMap((result: ImageDTO) => of(ProviderActions.LoadWaitingListAttachmentsCompleted({ payload: result }))),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.LoadWaitingListAttachmentsFailed({ error })
					])
				)
			);
		})
	),
		{ useEffectsErrorHandler: false }
	);

	uploadPhotos$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.UploadPhotos),
		switchMap(({ payload }) => {
			return this.waitingListService.uploadWaitingListAttachment(payload).pipe(
				switchMap((result: any) => of(ProviderActions.UploadPhotosCompleted({ payload: result }))),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.UploadPhotosFailed({ error })
					])
				)
			);
		})
	),
		{ useEffectsErrorHandler: false }
	);

	waitingListBookingStatus$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GetWaitingListBookingStatus),
		withLatestFrom(this.providerFacade.data$),
		filter(([action, provider]: [any, PersonDTO]) => !!provider && !!action.payload),
		switchMap(([action, provider]: [any, PersonDTO]) => {
			return this.waitingListService.getWaitingListBookingStatus(action.payload, provider.id).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: Array<WaitingListBookingStatusResponseDTO>) =>
					of(ProviderActions.GetWaitingListBookingStatusComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GetWaitingListBookingStatusFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	patchWaitingListRequest$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.PatchWaitingListRequest),
		switchMap(({ payload: { requestId, data } }) => {
			return this.waitingListService.patchWaitingList(requestId, data).pipe(
				takeUntil(this.actions$.pipe(ofType(ProviderActions.ClearState))),
				switchMap((next: any) =>
					of(ProviderActions.PatchWaitingListRequestComplete({ payload: next }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.PatchWaitingListRequestFailed({ error: error })
					])
				)
			);
		})),
		{ useEffectsErrorHandler: false }
	);

	/** ============================================== Provider Report ========================================================== */

	grouppedAppointmentCounts$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.GrouppedAppointmentCounts),
		withLatestFrom(this.providerFacade.reportFilters$, this.providerFacade.reportFiltersPagination$),
		filter(([_, filters, pagination]: [any, ReportsRequestDTO, { page: number, pageSize: number }]) => !!pagination && !!filters),
		switchMap(([_, filters, { page, pageSize }]) => {
			return this.reportsService.groupedAppointmentCounts(page, pageSize, filters).pipe(
				switchMap((response: ReportsGroupedDataDTO) =>
					of(ProviderActions.GrouppedAppointmentCountsComplete({ payload: response }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.GrouppedAppointmentCountsFailed({ error }),
					])
				)
			);
		})), { useEffectsErrorHandler: false }
	);

	totalAppointmentCounts$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.TotalAppointmentCounts),
		withLatestFrom(this.providerFacade.reportFilters$),
		filter(([_, filters]: [any, ReportsRequestDTO]) => !!filters),
		switchMap(([_, filters]) => {
			return this.reportsService.totalAppointmentCounts(filters).pipe(
				switchMap((response: ReportsTotalCountsDTO) =>
					of(ProviderActions.TotalAppointmentCountsComplete({ payload: response }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.TotalAppointmentCountsFailed({ error }),
					])
				)
			);
		})), { useEffectsErrorHandler: false }
	);

	exportAppointmentCounts$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ExportAppointmentCounts),
		withLatestFrom(this.providerFacade.reportFilters$),
		filter(([_, filters]: [any, ReportsRequestDTO]) => !!filters),
		switchMap(([_, filters]) => {
			return this.reportsService.exportAppointmentCounts(filters).pipe(
				switchMap((response: Blob) =>
					of(ProviderActions.ExportAppointmentCountsComplete({ payload: response }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ExportAppointmentCountsFailed({ error }),
					])
				)
			);
		})), { useEffectsErrorHandler: false }
	);

	reportsUserAppointments$ = createEffect(() => this.actions$.pipe(
		ofType(ProviderActions.ReportsUserAppointments),
		withLatestFrom(this.providerFacade.reportFilters$, this.providerFacade.userReportFiltersPagination$, this.providerFacade.data$),
		filter(([_, filters, pagination]) => !!pagination && !!filters),
		switchMap(([{username, filter}, filters, { page, pageSize }, provider]) => {
			const reportsUserAppointmentsRequestDTO = {
				careProviderId: provider.id,
				username,
				startDate: filters.startDate,
				endDate: filters.endDate,
        medicalServices: filters.medicalServices,
        treatmentCategoryIds: filters.treatmentCategoryIds,
        treatmentIds: filters.treatmentIds,
        resourceIds: filters.resourceIds,
				filter
			} as ReportsUserAppointmentsRequestDTO;
			return this.reportsService.getReportsUserAppointments(page, pageSize, reportsUserAppointmentsRequestDTO).pipe(
				switchMap((response: ReportsUserAppointmentsDTO) =>
					of(ProviderActions.ReportsUserAppointmentsComplete({ payload: response }))
				),
				catchError((error) =>
					from([
						ProviderActions.Error({ error }),
						ProviderActions.ReportsUserAppointmentsFailed({ error }),
					])
				)
			);
		})), { useEffectsErrorHandler: false }
	);

  adminInstitutionReportCount$ = createEffect(() => this.actions$.pipe(
      ofType(ProviderActions.LoadAdminInstitutionCounts),
      switchMap(action => {
        return this.reportsService.getAdminInstitutionCounts(action.payload.providerId, action.payload.startDate, action.payload.endDate).pipe(
          ignoreResultOnActions(this.actions$.pipe(ofType(ProviderActions.ClearState))),
          switchMap((result: ReportsAdminInstitutionCountsDTO) =>
            of(ProviderActions.LoadAdminInstitutionCountsComplete({ payload: result }))
          ),
          catchError((error) =>
            from([
              ProviderActions.Error({ error }),
              ProviderActions.LoadAdminInstitutionCountsFailed({ error: error })
            ])
          )
        );
      })),
    { useEffectsErrorHandler: false }
  );

	constructor(
		private actions$: Actions,
		private providerFacade: ProviderFacade,
		private personServiceApi: PersonService,
		private keyValuePairServiceApi: KeyValuePairService,
		private medicalServicesService: MedicalServiceService,
		private messageService: MessageService,
		private onlineAppointmentApi: OnlineAppointmentService,
		private resourceOverviewService: ResourceOverviewService,
		private classicAppointmentApi: ClassicAppointmentService,
		private messageTemplateApi: MessageTemplateService,
		private resourceService: ResourceService,
		private absenceHourService: AbsentHoursService,
		private workScheduleService: WorkScheduleService,
		private cmsServiceApi: ContentManagementSystemService,
		private settingsService: SettingsService,
		private periodicalDeletionService: PeriodicalDeletionService,
		private treatmentService: TreatmentsService,
		private treatmentAssociationService: TreatmentAssociationService,
		private treatmentCategoryService: TreatmentCategoryService,
		private patientService: PatientService,
		private userServiceApi: UserService,
		private reportsService: ReportsService,
		private waitingListService: WaitingListService,
		private datePipe: DatePipe
	) {
	}
}
